import React, { useEffect } from 'react'

import {
  ActionIcon,
  Alert,
  Flex,
  Group,
  Stack,
  Typography
} from '@tidbcloud/uikit'
import { toTimeRangeValue } from '@tidbcloud/uikit/biz'
import { IconRefreshCw02 } from '@tidbcloud/uikit/icons'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'

import UIKitProvider from '~/uikit-provider'
import { eventTracking } from '~/utils/telemetry'

import { ClusterPageShell } from '../migration_shared/components'
import { useClusterToClusterInfo } from '../migration_shared/types'
import { StatementProvider, useStatementContext } from '../utils/context'
import { getStatementContext } from '../utils/context-impl'
import { FEATURE_NAME } from '../utils/helpers'
import { ListTable } from './ListTable'
import { StatementFilters } from './StatementFitlers'
import { useStatementListUrlState } from './url-state'

const StatementList: React.FC<React.PropsWithChildren<unknown>> = () => {
  const navigate = useNavigate()
  useEffect(() => {
    document.title = `TiDB Clinic - SQL Repository`
  }, [])
  const ctx = useStatementContext()
  const { timeRange, kinds, search, sortRules, setSortRules } =
    useStatementListUrlState()

  const {
    data: stmtList,
    isLoading,
    isFetching,
    refetch: reloadStmtList
  } = useQuery(
    ['statement', 'stmt_list', ctx.cfg.clusterId, timeRange, kinds, search],
    () => {
      const [begin_time, end_time] = toTimeRangeValue(timeRange)
      return ctx.ds.getStmtList({
        begin_time: begin_time + '',
        end_time: end_time + '',
        kinds,
        search
      })
    },
    {
      refetchOnWindowFocus: false
    }
  )

  return (
    <ClusterPageShell
      title='SQL Repository'
      headerProps={{
        withBack: true,
        onBackClick() {
          navigate('..', { replace: true })
          eventTracking('SQL Repository Back Button Clicked')
        }
      }}
    >
      <Stack mx={-24}>
        <Alert ml={24} mb={8} color='peacock'>
          <Typography variant='body-lg' pr={8}>
            This page displays data from external storage with a longer history
            for enhanced diagnostics, though it may be delayed by up to 30
            minutes.
          </Typography>
        </Alert>

        <Flex px={24} justify='flex-start' wrap='wrap'>
          <StatementFilters
            isLoading={isLoading || isFetching}
            enableFetchData={true}
          />

          <Group ml='auto'>
            <ActionIcon
              aria-label='Refresh'
              data-testid='statement-refresh-button'
              variant='subtle'
              size='lg'
              onClick={() => {
                eventTracking(`${FEATURE_NAME} Refresh Button Clicked`)
                reloadStmtList()
              }}
            >
              <IconRefreshCw02 size={16} />
            </ActionIcon>
          </Group>
        </Flex>

        <ListTable
          data={stmtList ?? []}
          loading={isLoading || isFetching}
          sortBy={sortRules}
          onSortChange={setSortRules}
        />
      </Stack>
    </ClusterPageShell>
  )
}

/////////////////////////////////////////

interface StatementListPageProps {}

const StatementListPage: React.FC<
  React.PropsWithChildren<StatementListPageProps>
> = () => {
  const navigate = useNavigate()

  const clusterInfo = useClusterToClusterInfo()

  return (
    <UIKitProvider>
      <StatementProvider value={getStatementContext(clusterInfo, navigate)}>
        <StatementList />
      </StatementProvider>
    </UIKitProvider>
  )
}

// use React.memo to prevent re-rendering when the cluster is updating periodically
export default StatementListPage
